import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import config from '@iotv/config';
import { getClosestPartnerInclusiveItemTypeToGroupNestedAscendingQueryDef } from '@iotv/datamodel-core';
import LastPageIcon from '@mui/icons-material/LastPage';
import { Box, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { SimpleTreeView, treeItemClasses } from '@mui/x-tree-view';
import { useContext, useMemo } from 'react';
import { AlertErrors } from '../../../ErrorHandling/ErrorBoundaryFallback';
import { ActionIcons } from '../../../Iconography';
import { AppInfo } from '../../utils/AppInfo';
import { AppVersion } from '../../utils/AppVersion';
import { DefaultVentureViewSwitch } from '../../utils/DefaultVentureViewSwitch';
import { TopLevelMenu } from '../ContextMenuV2/TopLevelMenu';
import { NavigationContext, NavigationContextProvider } from '../NavigationContext';
import { viewArbitrator } from './ViewArbitrator';
import { getNoDomainMappingTreeDef } from './defaultMenuStructure';
import { instantiateTree } from './treeInstantiation';
const { NODE_ENV, REACT_APP_DEPLOYMENT_STAGE: stage } = process.env;
const isDevelopment = NODE_ENV === 'development' || stage === 'dev';
const NavMainInternal = ({ applicationData, contextCustomer, contextUser, UserFunctions, TransactionFunctions, roleArbitrationInstance, treeDefinitionMap, }) => {
    const navigationContext = useContext(NavigationContext);
    // Fetch partner
    // Partner will be in applicaton Data if we logged in as a partner because of a log in query on the partner role
    const contextPartner = useMemo(() => {
        if (!applicationData || !contextUser)
            return undefined;
        const partnerQuery = getClosestPartnerInclusiveItemTypeToGroupNestedAscendingQueryDef({
            sourceItemType: 'User',
            groupDefiningObjectTypeId: 'Partner',
        });
        const partners = applicationData?.applyBranchingQuery([contextUser], partnerQuery);
        return partners?.getNodesOfType('Partner')?.[0];
    }, [applicationData, contextUser]);
    const treeProps = {
        applicationData,
        contextCustomer,
        roleArbitrationInstance,
        transactionFunctions: TransactionFunctions,
        user: contextUser,
        userFunctions: UserFunctions,
        partner: contextPartner,
    };
    // in CAPS so it is more obvious where the tree comes from
    const TREE_ITEMS = instantiateTree({ treeDefinitionMap, treeProps, navigationContext });
    return (_jsxs(SimpleTreeView, { "aria-label": "customized", sx: { flexGrow: 1, [`.${treeItemClasses.content}, .${treeItemClasses.root} > div`]: { paddingBlock: '2px' } }, slots: { collapseIcon: ActionIcons.NavigationCollapse, expandIcon: ActionIcons.NavigationExpand }, children: [TREE_ITEMS.map((item, i) => (_jsx(AlertErrors, { children: item }, i))), _jsx(TopLevelMenu, { ...treeProps })] }, "mainTree"));
};
export const NavMain = ({ applicationData, roleArbitrationInstance, externalTreeDefinitionMap, domainTreeMapping: externalDomainTreeMapping, contextUser, contextCustomer, UserFunctions, TransactionFunctions, fullscreen, onFullscreenToggle, useSpecializedViews, externalMenuOrdering, }) => {
    /**
     * The CSS here is very particular to ensure that:
     * - the tab appears on the right side of the drawer flyout
     * - the tab does not cause the drawer to have horizontal scrolling
     * - the tab is agnostic to vertical scrolling of the drawer content
     * - the tab is not visible when the drawer is hidden
     */
    const unFullscreenButton = (_jsx(Box, { position: 'relative', top: 0, left: 0, right: 0, overflow: 'visible', sx: { containerType: 'inline-size' }, children: _jsx(Paper, { elevation: 0, sx: {
                position: 'fixed',
                left: '100cqw',
                borderStartStartRadius: 0,
                borderEndStartRadius: 0,
                borderStartEndRadius: '50%',
                borderEndEndRadius: '50%',
                padding: 1,
            }, children: _jsx(Tooltip, { title: fullscreen ? 'Keep drawer visible' : 'Allow drawer to be hidden', placement: 'left', arrow: true, children: _jsx(IconButton, { color: "inherit", "aria-label": "toggle fullscreen", onClick: onFullscreenToggle, children: _jsx(LastPageIcon, {}) }) }) }) }));
    const domainMap = config.app.webUI.domainMap ?? {};
    const domainTreeMapping = externalDomainTreeMapping ?? null; // Loaded into app context but might not exist
    /**
     * If domains are configured, but there is no external definition map, we shouldn't use the no-domain map
     *
     * ADH: memo'd because `getNoDomainMappingTreeDef()` returns a new object every time, so it will refresh the menu over and over
     */
    const resolvedTreeDefinitionMap = useMemo(() => externalTreeDefinitionMap ?? (Object.keys(domainMap).length === 0 ? getNoDomainMappingTreeDef() : null), [externalTreeDefinitionMap, domainTreeMapping]);
    const { menuTreePruningMap } = roleArbitrationInstance;
    /// TODO: treeDefinitionMap and TREE_ITEMS need to be useMemo'd with dep probably on roleArb to stop recalls
    const treeDefinitionMap = viewArbitrator({
        domainTreeMapping,
        domainMap,
        externalTreeDefinitionMap: resolvedTreeDefinitionMap,
        prunePathMap: menuTreePruningMap,
        externalMenuOrdering,
    }).treeDefinitionMap;
    return (_jsx(Box, { id: 'Navigation-Box', sx: { height: '100%', display: 'flex', paddingTop: 1, flexDirection: 'column' }, children: _jsxs(AlertErrors, { children: [fullscreen && unFullscreenButton, isDevelopment && _jsx(AppInfo, {}, 'appInfo'), _jsx(DefaultVentureViewSwitch, { useSpecializedViews, userFunctions: UserFunctions, roleArbitrationInstance }, `viewSwitch`), _jsx(Stack, { sx: { flexGrow: 1, overflowY: 'auto' }, children: _jsx(AlertErrors, { children: _jsx(NavigationContextProvider, { children: _jsx(NavMainInternal, { applicationData: applicationData, roleArbitrationInstance: roleArbitrationInstance, treeDefinitionMap: treeDefinitionMap, contextUser: contextUser, contextCustomer: contextCustomer, UserFunctions: UserFunctions, TransactionFunctions: TransactionFunctions }) }) }, 'e2') }), isDevelopment && _jsx(AppVersion, {}, 'appVersion')] }, 'e1') }, 'navigationBox'));
};
